import type { SupabaseClient } from '@supabase/supabase-js'

const PAGE_SIZE = 1000

interface FetchFilter {
  column: string
  op: 'gte' | 'lte' | 'eq'
  value: string
}

interface FetchAllOptions {
  table: string
  select: string
  filters?: FetchFilter[]
  orderBy?: { column: string; ascending: boolean }
}

/**
 * Fetch all rows from a Supabase table with automatic pagination.
 * Supabase PostgREST returns max 1000 rows by default. This function
 * loops with .range() until all matching rows are retrieved.
 */
export async function fetchAllRows<T = Record<string, unknown>>(
  client: SupabaseClient,
  options: FetchAllOptions
): Promise<{ data: T[]; error: Error | null }> {
  const { table, select, filters, orderBy } = options
  const allRows: T[] = []
  let page = 0

  while (true) {
    const from = page * PAGE_SIZE
    const to = from + PAGE_SIZE - 1

    let query = client.from(table).select(select).range(from, to)

    if (orderBy) {
      query = query.order(orderBy.column, { ascending: orderBy.ascending })
    } else {
      query = query.order('created_at', { ascending: true })
    }

    if (filters) {
      for (const f of filters) {
        if (f.op === 'gte') query = query.gte(f.column, f.value)
        else if (f.op === 'lte') query = query.lte(f.column, f.value)
        else if (f.op === 'eq') query = query.eq(f.column, f.value)
      }
    }

    const { data, error } = await query

    if (error) {
      return { data: allRows, error: new Error(error.message) }
    }

    if (data) {
      allRows.push(...(data as T[]))
    }

    if (!data || data.length < PAGE_SIZE) break
    page++
  }

  return { data: allRows, error: null }
}
